import React, { FC, useEffect, useMemo, useState } from "react";
import SalarySimService from "../services/SalarySimService";
import { UseSalarySimContext } from "../Context/SalarySimContext";
import { UseConfigContext } from "../Context/ConfigContext";
import { ResultFormProps, SalSimFormProps } from "../FormModel/FormStepProps";
import ResultElementComponent from "./ResultElementComponent";
import ResultSectionComponent from "./ResultSectionComponent";
import SalarySimInputPostData from "../Model/SalarySimInputPostData";
import ResultData, { Cl_Pacible } from "../Model/ResultData";
import { UseCodeListContext } from "../Context/CodeListContext";
import { UseTranslationContext } from "../Context/TranslationContext";
import useMediaQuery from '@mui/material/useMediaQuery';
import ResultPrint  from "./ResultPrintComponent";
import ServeDocument from "../services/DocgenService";
import axios, {AxiosError} from 'axios';
import { Download } from "@mui/icons-material";
import Lottie from "lottie-react";
import spinner from "../FormModel/lottie_spinner_blue.json";
import { UseToastContext, toastType } from "../Context/ToastContext";
import { DateTime } from "luxon";
import { FormattedValue } from "../hooks/dataFormatter";

const Step3SimpleResult: FC<ResultFormProps> = (props) => {

    const { getKeyFromKeyId, currentLanguage } = UseTranslationContext();
    const { getServiceUrl } = UseConfigContext();
    const { popToast } = UseToastContext();

    const { getCLPrestaKey } = UseCodeListContext();
    const { salarySimFormData, updateSalarySimData, salarySimInputMappedData, salarySimResultData, isSimpleSimulationMode } = UseSalarySimContext();
    
    const isAtBreakpoint = useMediaQuery('screen and (max-width: 479px)');
    const [selectedPanel, setSelectedPanel] = useState(0);
    const [isExportDisabled, setIsExportDisabled] = useState(false);

    // To be cleaned up/removed once we're 3 months past April 2024
    const isR1WUsed: Boolean = useMemo(() => {
        const currentDate: DateTime = DateTime.now().set({ day: 1 });
        if (currentDate.year < 2024) return true;
        let outputDate: DateTime = currentDate;
        const limitDate: DateTime = DateTime.fromObject({ year: 2024, month: 4, day: 1});
        outputDate = outputDate.set({ month: salarySimInputMappedData.mois_periode_paie });
        return outputDate < limitDate;
    }, [salarySimInputMappedData]);

    const handlePanelSelect = (event: any) => {
        setSelectedPanel(event.target.value);
    };

    const formatNumber = (data: number) => (isNaN(data) || data == null || data == undefined) ? FormattedValue(0) : FormattedValue(data);

    enum panelVisibility {
        mobileOnly,
        fullOnly,
        both
    };

    const getPanelVisibility = (panel: number, target?: panelVisibility) => {
        if (target == undefined) target = panelVisibility.both;
        let isVisible = isAtBreakpoint ?
            target != panelVisibility.fullOnly && panel == selectedPanel :
            target != panelVisibility.mobileOnly;
        
        let cssProp = ({display: isVisible ? 'block' : 'none'});
        return cssProp as React.CSSProperties;
    };
    
    function GetErrorMessage(lang: string) {
        switch (lang) {
            case 'fr':
                return 'IBRUNET_INDISPONIBLE';
                break;
            case 'nl':
                return 'IBRUNET_INDISPONIBLE';
                break;
            case 'en':
            default:
                return 'IBRUNET_INDISPONIBLE';
                break;
        }
    }

    const GetFirstEmployeeTitle = () => {
        switch (salarySimInputMappedData.Scenario_1er_Engage) {
            case 1:
                return "RECUD_1ER_TRAV";
            case 2:
                return "REDUC_2EME_TRAV";
            case 3:
                return "REDUC_3EME_TRAV";
            case 4:
                return "REDUC_4EME_TRAV";
            case 5:
                return "REDUC_5EME_TRAV";
            case 6:
                return "REDUC_6EME_TRAV";
            default:
                return "";
        }
    }

    const GenerateSingleFirstEmployeeScenarioLayout = (scenario: Cl_Pacible, index: number) => {
        if (scenario.No_Scenario == salarySimInputMappedData.Scenario_1er_Engage) {
            if (scenario.Total_Trimestre == 999) {
                return <ResultElementComponent key={"scenarEngag" + index} modelText={getKeyFromKeyId("DUREE_INDETERMINEE")} modelValue={scenario.Montant} modelType={"SINGLE_LINE_VALUE"} />
            }
            else
            {
                return <ResultElementComponent key={"scenarEngag" + index} modelText={`- ${scenario.Total_Trimestre} ${getKeyFromKeyId("TRIMESTRES")}`} modelValue={scenario.Montant} modelType={"SINGLE_LINE_VALUE"} />
            }
        }
    }

    const GenerateScenarioFirstEmployee = () => {
        if (salarySimInputMappedData.Scenario_1er_Engage != 0 && salarySimResultData.Scenario_1er_Engage != null && salarySimResultData.Scenario_1er_Engage.length > 0) {
            return (
                <>
                    <ResultSectionComponent modelText={getKeyFromKeyId("REDUC_1ER_ENGAGE")} modelValue={0} modelType={"SECTION_TITLE"} />
                    <ResultElementComponent modelText={getKeyFromKeyId(GetFirstEmployeeTitle())} modelValue={0} modelType={"SINGLE_LINE"} />

                    {salarySimResultData.Scenario_1er_Engage.map((scenario, i) => GenerateSingleFirstEmployeeScenarioLayout(scenario, i)
                    )}
                </>
            );
        }
    }

    const GenerateR6SOnssInfo = () => {

        var messageInfo = getKeyFromKeyId("REMARQUE_ONSS");
        var hh = salarySimResultData.R6S / 60;
        var mm = salarySimResultData.R6S % 60;
        var hhmm = hh + ":" + mm;

        messageInfo = messageInfo.replace("#jours#",salarySimResultData.R6R.toString());
        messageInfo = messageInfo.replace("#heures#",hhmm);

        return messageInfo;
    }

    const ExportDocument = () => {
        Promise.resolve(setIsExportDisabled(true))
            .then(() => {
                if (!salarySimResultData.resultOK)
                    throw getKeyFromKeyId("NO_SIM_EXPORTABLE");
            })
            .then(() => ServeDocument(getServiceUrl(), salarySimInputMappedData, salarySimResultData, currentLanguage))
            .then(response => {
                let linkSource = `data:application/pdf;base64,${response.data}`;
                let anchorLink = document.createElement("a");
                let filename = "SalarySim.pdf";
                anchorLink.href = linkSource;
                anchorLink.download = filename;
                anchorLink.click();
            })
            .catch((error: Error | AxiosError) => {
                popToast(toastType.Error, error.message ?? error);
            })
            .finally(() => setIsExportDisabled(false));
    };

    /*--------------------------------------------------------*/
    return (
        <>
            <div style={{ display: (props.isCurrentForm ? '' : 'none') }}>
                {/* PRINT */}
                <ResultPrint />

                {/* RESULT DISPLAY */}
                <div className={isSimpleSimulationMode?"alignEnd exportButtonAlign no-print":"alignEnd exportButtonAlignAdv no-print"}>
                <button type="button" id="button-export" className={`button-gost mobile w-inline-block exportButton ${isExportDisabled ? "disabled" : ""} no-print`} disabled={isExportDisabled} onClick={() => ExportDocument()}>
                    <p className="button-text-gost">{getKeyFromKeyId("EXPORT_DOC")}</p>
                    { isExportDisabled && 
                        <Lottie animationData={spinner} autoPlay={true} loop={true} className="lottie-animation" />
                    }
                </button>
                </div>
                <div id="printable-table" aria-label="Section &quot;résultats&quot;" tabIndex={0} className="w-layout-grid table-mobile">
                    <div className="select-resultat w-form">
                        <select id="options-r-sultat" name="options-r-sultat" data-name="options-résultat" className="list-result w-select" onChange={handlePanelSelect}>
                            <option value={0}>{getKeyFromKeyId("RESULT_WORKER")}</option>
                            <option value={1}>{getKeyFromKeyId("RESULT_EMPLOYER")}</option>
                        </select>
                    </div>
                    {/* ---- */}
                    <div style={getPanelVisibility(0, panelVisibility.fullOnly)} id="result-arecevoir" className="resultat w-node-_1f93777d-edec-3952-4339-fb2c64a6da67-4b01152a">
                        <div id="w-node-bf761b13-53c6-1b2e-46ff-b6dcce2e76e6-4b01152a" className="title-result">
                            <h3 className="title-table-result">{`A - ${getKeyFromKeyId("RESULT_WORKER")}`}<br />(F+G-H)<br /></h3>
                        </div>
                        <div id="result-arecevoir-data" aria-label={getKeyFromKeyId("RESULT_WORKER")} className="score">{formatNumber(salarySimResultData.T80)}</div>
                    </div>
                    <div style={getPanelVisibility(1, panelVisibility.fullOnly)} id="result-apayer" className="resultat w-node-_0a7dbbfb-99ca-0aab-965e-d0d71884efa4-4b01152a">
                        <div id="w-node-_0a7dbbfb-99ca-0aab-965e-d0d71884efa5-4b01152a" className="title-result">
                            <h4 className="title-table-result">{`I - ${getKeyFromKeyId("RESULT_EMPLOYER")}`}<br />(1+2+3)<br /></h4>
                        </div>
                        <div id="result-apayer-data" aria-label={getKeyFromKeyId("RESULT_EMPLOYER")} className="score">{formatNumber(salarySimResultData.TOTSS)}</div>
                    </div>
                    <div style={getPanelVisibility(0, panelVisibility.mobileOnly)} id="result-arecevoir-mobil" className="resultat-mobile w-node-_80e67cc9-1113-708c-a05b-f7fc13b3e500-4b01152a">
                        <div id="w-node-_80e67cc9-1113-708c-a05b-f7fc13b3e501-4b01152a" className="title-result">
                            <h3 className="title-table-result">{`${getKeyFromKeyId("TOTAL")} (F+G-I)`}<br /></h3>
                        </div>
                        <div id="result-arecevoir-data-mobile" className="score">{formatNumber(salarySimResultData.T80)}</div>
                    </div>
                    <div style={getPanelVisibility(1, panelVisibility.mobileOnly)} id="result-apayer-mobil" className="resultat-mobile ordre w-node-_51055941-4590-b9e8-a762-01f477cd26bd-4b01152a">
                        <div className="title-result">
                            <h3 className="title-table-result">{`${getKeyFromKeyId("TOTAL")} (1+2+3)`}<br /></h3>
                        </div>
                        <div id="result-apayer-data-mobil" className="score">{formatNumber(salarySimResultData.TOTSS)}</div>
                    </div>

                    {/* START OF RESULT PANEL */}
                    <div id="column-left" style={getPanelVisibility(0, panelVisibility.both)} className="column w-node-a984c9c8-635d-7bb1-a7cb-89e6562edd43-4b01152a">
                        <section id="section-b" className="section-table">
                            { /* B Brut */}
                            <ResultSectionComponent modelText={`B - ${getKeyFromKeyId("BRUT")}`} modelValue={0} modelType={"SECTION_TITLE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("BRUT_BASE")} modelValue={salarySimInputMappedData.brut} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("COMMISSIONS")} modelValue={salarySimInputMappedData.commissions} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("AVANT_NAT")} modelValue={salarySimInputMappedData.avantages_nature} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("AVANT_NAT_VOIT")} modelValue={salarySimInputMappedData.avantages_nature_voiture} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("MONTANT_IMPOSABLE_PP_ORDINAIRE_LG")} modelValue={salarySimInputMappedData.Montant_Imposable_Pp_Ordinaire} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("DROITS_AUTEUR")} modelValue={salarySimInputMappedData.Droits_Auteur} modelType={"SINGLE_LINE_VALUE"} />
                            {/* Total Brut */}
                            <ResultElementComponent modelText={getKeyFromKeyId("TOTAL")} modelValue={salarySimResultData.T10} modelType={"SINGLE_LINE_VALUE_TOTAL"} />
                        </section>
                        <section id="section-c" className="section-table">
                            { /* C Retenues */}
                            <ResultSectionComponent modelText={`C - ${getKeyFromKeyId("RETENUES")}`} modelValue={0} modelType={"SECTION_TITLE"} />

                            <ResultElementComponent modelText={getKeyFromKeyId("COT_PERS_REMUN_ORDI")} modelValue={salarySimResultData.R10} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("COT_PERS_REMUN_EXCEP")} modelValue={salarySimResultData.R11} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("COT_PERS_INDEMN_RUPT")} modelValue={salarySimResultData.R14} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("COT_PERS_BRUT_SOUMIS_PP")} modelValue={salarySimResultData.R19} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("RETENUE_ONSS_DROITS_AUTEUR")} modelValue={salarySimResultData.R1M} modelType={"SINGLE_LINE_VALUE"} />
                            
                            {
                                isR1WUsed &&
                                    <ResultElementComponent modelText={getKeyFromKeyId("BONUS_EMPLOI")} modelValue={salarySimResultData.R1W} modelType={"SINGLE_LINE_VALUE"} />
                            }

                            { 
                                !isR1WUsed &&
                                <>
                                    <ResultElementComponent modelText={getKeyFromKeyId("BONUS_EMPLOI_A")} modelValue={salarySimResultData.R1R} modelType={"SINGLE_LINE_VALUE"} />
                                    <ResultElementComponent modelText={getKeyFromKeyId("BONUS_EMPLOI_B")} modelValue={salarySimResultData.R1S} modelType={"SINGLE_LINE_VALUE"} />
                                </>
                            }
                            
                            <ResultElementComponent modelText={getKeyFromKeyId("SPORTBONUS")} modelValue={salarySimResultData.R1U} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("COT_SEC_SOC_PART")} modelValue={salarySimResultData.R1Y} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("COT_SOLID_DPV")} modelValue={salarySimResultData.R1Z} modelType={"SINGLE_LINE_VALUE"} />

                            {/* Total Retenues */}
                            <ResultElementComponent modelText={getKeyFromKeyId("TOTAL")} modelValue={salarySimResultData.T20} modelType={"SINGLE_LINE_VALUE_TOTAL"} />
                        </section>
                        <section id="section-d" className="section-table sum">
                            {/* D Imposable B - C */}
                            <ResultSectionComponent modelText={`D - ${getKeyFromKeyId("TAXABLE")} (B-C)`} modelValue={salarySimResultData.T30} modelType={"SECTION_TITLE_VALUE"} />
                        </section>
                        <section id="section-e" className="section-table">
                            { /* E précompte fiscal */}
                            {salarySimInputMappedData.frais_deplacement > 0 ?
                                <ResultSectionComponent modelText={`E - ${getKeyFromKeyId("PRECOMPTE_FISCAL")}`} modelValue={0} modelType={"SECTION_TITLE"} modelTooltip={getKeyFromKeyId("TOOLTIP_IMPOSABLE")} modelTooltipAlt={getKeyFromKeyId("TOOLTIP_IMPOSABLE_ALT")} modelTooltipPlacement="right" /> :
                                <ResultSectionComponent modelText={`E - ${getKeyFromKeyId("PRECOMPTE_FISCAL")}`} modelValue={0} modelType={"SECTION_TITLE"} />
                            }

                            <ResultElementComponent modelText={getKeyFromKeyId("REMUN_ORDINAIRE")} modelValue={salarySimResultData.R30} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("ALLOC_EXCEP")} modelValue={salarySimResultData.R31} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("PECULE_VAC")} modelValue={salarySimResultData.TOTR32} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("INDEMINITE_RUPT")} modelValue={salarySimResultData.R34} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("AUTRES")} modelValue={salarySimResultData.T40A} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("PRECOMPTE_MOBILIER_DROITS_AUTEUR")} modelValue={salarySimResultData.Somme_R4I_R4J} modelType={"SINGLE_LINE_VALUE"} />

                            {/* Total précompte */}
                            <ResultElementComponent modelText={getKeyFromKeyId("TOTAL")} modelValue={salarySimResultData.T40} modelType={"SINGLE_LINE_VALUE_TOTAL"} />
                        </section>
                        <section id="section-f" className="section-table sum">
                            { /* F Net D - E */}
                            <ResultSectionComponent modelText={`F - ${getKeyFromKeyId("NET")} (D-E)`} modelValue={salarySimResultData.T50} modelType={"SECTION_TITLE_VALUE"} />
                        </section>
                        <section id="section-g" className="section-table">
                            { /* G Divers + */}
                            <ResultSectionComponent modelText={`G - ${getKeyFromKeyId("MISCELLANEOUS")} [ + ]`} modelValue={0} modelType={"SECTION_TITLE"} />

                            <ResultElementComponent modelText={getKeyFromKeyId("ABON_SOCIAL_PUBLIC")} modelValue={salarySimInputMappedData.abonnement_social} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("ABON_SOCIAL_PRIVE")} modelValue={salarySimInputMappedData.frais_deplacement} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("DIVERS_NETS_POSITIFS")} modelValue={salarySimInputMappedData.divers_net_pos} modelType={"SINGLE_LINE_VALUE"} />
                            { /*  total divers G +*/}
                            <ResultElementComponent modelText={getKeyFromKeyId("TOTAL")} modelValue={salarySimResultData.T60} modelType={"SINGLE_LINE_VALUE_TOTAL"} />
                        </section>
                        <section id="section-h" className="section-table">
                            { /* H Divers - */}
                            <ResultSectionComponent modelText={`H - ${getKeyFromKeyId("MISCELLANEOUS")} [ - ]`} modelValue={0} modelType={"SECTION_TITLE"} />

                            <ResultElementComponent modelText={getKeyFromKeyId("COT_SPEC_SEC_SOC")} modelValue={salarySimResultData.R82} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("AVANT_NAT")} modelValue={salarySimInputMappedData.avantages_nature} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("AVANT_NAT_VOIT")} modelValue={salarySimInputMappedData.avantages_nature_voiture} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("ASSUR_GROUPE")} modelValue={salarySimInputMappedData.assurance_groupe} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={`${getKeyFromKeyId("DIVERS_NET")} [ - ]`} modelValue={salarySimInputMappedData.divers_net_neg} modelType={"SINGLE_LINE_VALUE"} />

                            { /* total Divers H - */}
                            <ResultElementComponent modelText={getKeyFromKeyId("TOTAL")} modelValue={salarySimResultData.T70} modelType={"SINGLE_LINE_VALUE_TOTAL"} />
                        </section>
                    </div>
                    { /*  A payer - employeur */}
                    <div id="column-right" style={getPanelVisibility(1, panelVisibility.both)} className="column ordre w-node-_979b49e2-1753-59ae-2770-110b65367e26-4b01152a">
                        <section id="section-1" className="section-table">

                            { /* 1 Retenue sur paie */}
                            <ResultSectionComponent modelText={`1 - ${getKeyFromKeyId("RETENUE_PAIE")}`} modelValue={0} modelType={"SECTION_TITLE"} />

                            <ResultElementComponent modelText={getKeyFromKeyId("COT_ONSS_PERS")} modelValue={salarySimResultData.T20} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("COT_SPEC_SEC_SOC")} modelValue={salarySimResultData.R82} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("PRECOMPTE_FISCAL")} modelValue={salarySimResultData.T40} modelType={"SINGLE_LINE_VALUE"} />

                            { /* total 1 */}
                            <ResultElementComponent modelText={getKeyFromKeyId("TOTAL")} modelValue={salarySimResultData.TOTRP} modelType={"SINGLE_LINE_VALUE_TOTAL"} />
                        </section>
                        <section id="section-2" className="section-table">
                            { /* 2 Cotisations patronales */}
                            <ResultSectionComponent modelText={`2 - ${getKeyFromKeyId("COT_PATRONALE")}`} modelValue={0} modelType={"SECTION_TITLE"} />

                            <ResultElementComponent modelText={getKeyFromKeyId("CHARG_PAT_SEC_SOC")} modelValue={salarySimResultData.PA1} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("CHARG_PAT_ASSURANCE")} modelValue={salarySimInputMappedData.assurance_groupe_empl} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getKeyFromKeyId("COT_PAT_OPOC")} modelValue={salarySimResultData.PA4} modelType={"SINGLE_LINE_VALUE"} />
                            <ResultElementComponent modelText={getCLPrestaKey(salarySimResultData.Premier_Code_Pa5)} modelValue={-salarySimResultData.Premier_Code_Pa5_Montant} modelType={"SINGLE_LINE_VALUE"} />
                            
                            {/*  Réductions 2ème code trouvé dans PA5 */}
                            {(salarySimResultData.Second_Code_Pa5 != null && salarySimResultData.Second_Code_Pa5_Montant != 0) &&
                                <ResultElementComponent modelText={getCLPrestaKey(salarySimResultData.Second_Code_Pa5)} modelValue={-salarySimResultData.Second_Code_Pa5_Montant} modelType={"SINGLE_LINE_VALUE"} />
                            }

                            {/* Total cotisations patronales */}
                            <ResultElementComponent modelText={getKeyFromKeyId("TOTAL")} modelValue={salarySimResultData.TOTCP} modelType={"SINGLE_LINE_VALUE_TOTAL"} />
                        </section>

                        <section id="section-2-bis" className="section-table sum">
                            { /* 3 net à recevoir */}
                            <ResultSectionComponent modelText={`3 - ${getKeyFromKeyId("NET_RECEIVE_EMPL")} (A)`} modelValue={salarySimResultData.T80} modelType={"SECTION_TITLE_VALUE"} />
                        </section>

                        <section id="section-apayer" className="section-table sum">
                            { /* A payer via le secr.soc. (1 + 2) */}
                            <ResultSectionComponent modelText={`${getKeyFromKeyId("PAY_SECSOC")} (1+2)`} modelValue={salarySimResultData.TOT12} modelType={"SECTION_TITLE_VALUE"} />
                        </section>
                            
                        <section id="section-3" className="section-table">
                            { /* réduction maribel */}
                            {salarySimResultData.P1C != 0 &&
                                <>
                                    <ResultElementComponent modelText={getKeyFromKeyId("MARIBELSOCIAL")} modelValue={-salarySimResultData.P1C} modelType={"SINGLE_LINE_VALUE"} />
                                </>
                            }
                        </section>

                        <section id="section_a_tester" className="section-table">
                            { /* Affichage des scénarios 1er engagement */}
                            {GenerateScenarioFirstEmployee()}
                        </section>
                    </div>
                </div>
                <div className={"grid"}>
                    <p className={"maribelPanel legend"}>{GenerateR6SOnssInfo()}</p>
                </div>
                <div style={{ display: (salarySimResultData.P1C == 0?'none' : 'flex') }} className={"grid maribelGrid"}>
                    <h3 className={"maribelPanel maribelTitle"}>{getKeyFromKeyId("MARIBELSOCIAL")}</h3>
                    <p className={"maribelPanel legend"}>
                        
                        {getKeyFromKeyId("MARIBELSOCIAL_P1HTML")}
                        <br/><br/>
                        {getKeyFromKeyId("MARIBELSOCIAL_P2HTML")}
                        <br/><br/>
                        {getKeyFromKeyId("MARIBELSOCIAL_P3HTML")}
                        <br/><br/>
                        {getKeyFromKeyId("MARIBELSOCIAL_P4HTML")}
                    </p>
                </div>
            </div>
        </>
    );
}

export default Step3SimpleResult;